import { CATEGORY_MAP, MODEL_MAP, type Product, type CategoryId, type ModelId } from "./catalog";
// Re-export used types so consumers may narrow safely

export type SortMode = "relevance" | "price-asc" | "price-desc" | "newest" | "rating";

export interface ShopFilters {
  q: string;
  mode: "keyword" | "oem";
  brand: string;
  models: string[];
  category: string;
  sort: SortMode;
  inStock: boolean;
  min: number;
  max: number;
}

export interface ProductMatch {
  product: Product;
  score: number;
  matchedOem?: string;
  quality: "exact" | "prefix" | "fuzzy" | "keyword" | "none";
}

/** Strip whitespace, dashes, dots, slashes; uppercase. */
export function normalizeOem(input: string): string {
  return input.replace(/[\s\-./_]+/g, "").toUpperCase();
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const v0 = new Array(b.length + 1).fill(0).map((_, i) => i);
  const v1 = new Array(b.length + 1).fill(0);
  for (let i = 0; i < a.length; i++) {
    v1[0] = i + 1;
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
    }
    for (let j = 0; j <= b.length; j++) v0[j] = v1[j];
  }
  return v1[b.length];
}

function scoreOem(product: Product, query: string): ProductMatch {
  const q = normalizeOem(query);
  if (!q) return { product, score: 0, quality: "none" };
  let best: ProductMatch = { product, score: 0, quality: "none" };
  for (const ref of product.oem) {
    const n = normalizeOem(ref);
    if (n === q) return { product, score: 100, matchedOem: ref, quality: "exact" };
    if (q.length >= 4 && n.startsWith(q)) {
      if (best.score < 80) best = { product, score: 80, matchedOem: ref, quality: "prefix" };
      continue;
    }
    if (n.includes(q) && q.length >= 4) {
      if (best.score < 60) best = { product, score: 60, matchedOem: ref, quality: "prefix" };
      continue;
    }
    if (q.length >= 5) {
      const d = levenshtein(q, n);
      if (d <= 2) {
        const s = 50 - d * 10;
        if (best.score < s) best = { product, score: s, matchedOem: ref, quality: "fuzzy" };
      }
    }
  }
  // Also try SKU
  const sku = normalizeOem(product.sku);
  if (sku === q) return { product, score: 95, matchedOem: product.sku, quality: "exact" };
  if (q.length >= 4 && sku.includes(q) && best.score < 70) {
    best = { product, score: 70, matchedOem: product.sku, quality: "prefix" };
  }
  return best;
}

function scoreKeyword(product: Product, query: string): ProductMatch {
  const q = query.trim().toLowerCase();
  if (!q) return { product, score: 1, quality: "keyword" };
  const tokens = q.split(/\s+/).filter(Boolean);
  const haystacks = [
    product.name.toLowerCase(),
    product.brand.toLowerCase(),
    product.sku.toLowerCase(),
    product.oem.join(" ").toLowerCase(),
    (CATEGORY_MAP[product.category as CategoryId]?.label ?? String(product.category)).toLowerCase(),
    product.models.map((m) => (MODEL_MAP[m as ModelId]?.label ?? String(m)).toLowerCase()).join(" "),
    product.badges.join(" ").toLowerCase(),
    (product.slug ?? "").toLowerCase(),
  ];
  const fullHay = haystacks.join(" | ");
  let score = 0;
  for (const t of tokens) {
    if (!fullHay.includes(t)) return { product, score: 0, quality: "none" };
    if (product.name.toLowerCase().startsWith(t)) score += 10;
    if (product.name.toLowerCase().includes(t)) score += 5;
    if (product.sku.toLowerCase().includes(t)) score += 6;
    if (product.oem.some((o) => o.toLowerCase().includes(t))) score += 8;
    score += 1;
  }
  return { product, score, quality: "keyword" };
}

export function searchProducts(products: Product[], filters: ShopFilters): ProductMatch[] {
  const q = filters.q.trim();
  let matches: ProductMatch[] = products.map((p) =>
    q
      ? filters.mode === "oem"
        ? scoreOem(p, q)
        : scoreKeyword(p, q)
      : { product: p, score: 1, quality: "keyword" },
  );

  if (q) matches = matches.filter((m) => m.score > 0);

  matches = matches.filter(({ product: p }) => {
    if (filters.brand !== "all" && p.brand !== filters.brand) return false;
    if (filters.models.length && !p.models.some((m) => filters.models.includes(m))) return false;
    if (filters.category !== "all" && p.category !== filters.category) return false;
    if (filters.inStock && !p.inStock) return false;
    if (filters.min > 0 && p.price < filters.min) return false;
    if (filters.max > 0 && p.price > filters.max) return false;
    return true;
  });

  switch (filters.sort) {
    case "price-asc":
      matches.sort((a, b) => a.product.price - b.product.price);
      break;
    case "price-desc":
      matches.sort((a, b) => b.product.price - a.product.price);
      break;
    case "rating":
      matches.sort((a, b) => b.product.rating - a.product.rating);
      break;
    case "newest":
      matches.sort((a, b) => b.product.addedAt.localeCompare(a.product.addedAt));
      break;
    default:
      matches.sort((a, b) => b.score - a.score || b.product.rating - a.product.rating);
  }

  return matches;
}

/**
 * Live dropdown suggestions: search in BOTH modes while typing so SKU/OEM
 * codes and plain keywords resolve without the user touching a toggle.
 * Returns the top matches plus the total match count.
 */
export function liveSuggestMatches(
  products: Product[],
  query: string,
  limit = 2,
): { items: ProductMatch[]; total: number } {
  const q = query.trim();
  if (q.length < 2) return { items: [], total: 0 };
  const base = { brand: "all", models: [], category: "all", sort: "relevance" as const, inStock: false, min: 0, max: 0 };
  const kw = searchProducts(products, { ...base, q, mode: "keyword" });
  const oem = searchProducts(products, { ...base, q, mode: "oem" });
  const best = new Map<string, ProductMatch>();
  for (const m of [...oem, ...kw]) {
    const cur = best.get(m.product.id);
    if (!cur || m.score > cur.score) best.set(m.product.id, m);
  }
  const merged = [...best.values()]
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score || b.product.rating - a.product.rating);
  return { items: merged.slice(0, limit), total: merged.length };
}

/** True when the query looks like an OEM/SKU code rather than plain words. */
export function looksLikeOem(query: string): boolean {
  const q = query.trim();
  const n = normalizeOem(q);
  return n.length >= 5 && /[0-9]/.test(n) && !/\s{2,}/.test(q);
}

/** Suggest nearest OEM prefix matches when a search yields nothing. */
export function suggestNearestOem(products: Product[], query: string, limit = 5): ProductMatch[] {
  const q = normalizeOem(query);
  if (q.length < 3) return [];
  const out: ProductMatch[] = [];
  for (let len = q.length - 1; len >= 3 && out.length < limit; len--) {
    const prefix = q.slice(0, len);
    for (const p of products) {
      if (out.find((m) => m.product.id === p.id)) continue;
      for (const ref of p.oem) {
        if (normalizeOem(ref).startsWith(prefix)) {
          out.push({ product: p, score: len, matchedOem: ref, quality: "fuzzy" });
          break;
        }
      }
      if (out.length >= limit) break;
    }
  }
  return out;
}